import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import Sidebar from './Sidebar';
import SidebarMenu from './SidebarMenu';
import Header from './Header';
import Footer from './Footer';
import PasswordExpiryNotice from './PasswordExpiryNotice';
import SandboxBanner from './SandboxBanner';
import useLayoutBucket from '../../hooks/useLayoutBucket';
import { isOffCanvasBucket, sidebarOffset, mainPadding, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED } from './shellMetrics';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* FE-08 — dar ekranda 0: kenar çubuğu off-canvas olur, içeriğin ÜSTÜNE biner
     ve yer kaplamaz. Eskiden burası koşulsuz 260px'ti; 390px'lik telefonda
     içerik alanını 130px'e düşüren satır buydu. */
  margin-left: ${({ $sidebarWidth }) => $sidebarWidth}px;
  transition: margin-left 0.25s ease;
  overflow: hidden;
  /* Flex çocuğun varsayılan "min-width: auto" değeri, içerideki geniş bir tablonun
     kabı ESNETMESİNE izin verir; o zaman dar ekranda yatay kaydırma tablodan
     SAYFAYA taşar (iç içe scroll yasağının yatay karşılığı). 0'a çekmek
     taşmayı tablonun kendi kabında tutar. */
  min-width: 0;
`;

const Main = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.bgColor};
  padding: ${({ $pad }) => $pad}px;

  /* Altın layout kuralı: yalnız içerik kayar, footer sabit durur.
     position: relative ŞART — aksi halde içerideki position:absolute torunların
     kapsayıcı bloğu main değil BELGE olur, overflow:auto onları kırpamaz ve
     belgeyi uzatırlar; o zaman footer da birlikte kayar.
     Somut örnek: MUIDataTable'ın ekran okuyucu için ürettiği 1px
     .MUIDataTable-liveAnnounce öğesi tablonun altına konumlanıyor ve uzun
     sayfalarda belgeyi yüzlerce piksel uzatıyordu. */
  position: relative;
`;

// FE-08 — off-canvas kenar çubuğunun perdesi. Yalnız dar ekranda ve yalnız
// çubuk açıkken var olur; tıklanınca kapatır.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  z-index: 1099;
  border: none;
  padding: 0;
`;

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bucket = useLayoutBucket();
  const offCanvas = isOffCanvasBucket(bucket);

  const width = offCanvas ? 0 : (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Masaüstüne genişleyince off-canvas durumu asılı kalmamalı: perde ve
  // kaydırma kilidi ekran genişlediğinde kendiliğinden temizlenir.
  useEffect(() => {
    if (!offCanvas && mobileOpen) setMobileOpen(false);
  }, [offCanvas, mobileOpen]);

  // Esc ile kapatma — off-canvas menü bir diyalog katmanı gibi davranır.
  useEffect(() => {
    if (!offCanvas || !mobileOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [offCanvas, mobileOpen]);

  return (
    <PageWrapper>
      <Sidebar
        collapsed={collapsed}
        offCanvas={offCanvas}
        open={mobileOpen}
        // Dar ekranda logo "daralt" değil "kapat" düğmesidir: daraltılmış mod
        // off-canvas modda zaten uygulanmıyor.
        onToggle={offCanvas ? closeMobile : () => setCollapsed(!collapsed)}
      >
        <SidebarMenu collapsed={offCanvas ? false : collapsed} onNavigate={offCanvas ? closeMobile : undefined} />
      </Sidebar>
      {offCanvas && mobileOpen && <Backdrop onClick={closeMobile} data-testid="sidebar-backdrop" />}
      <ContentArea $sidebarWidth={width}>
        <Header
          sidebarCollapsed={collapsed}
          showMenuButton={offCanvas}
          compact={bucket === 'xs' || bucket === 'sm'}
          onMenuClick={() => setMobileOpen((v) => !v)}
        />
        <SandboxBanner />
        <PasswordExpiryNotice />
        <Main $pad={mainPadding(bucket)}>{children}</Main>
        <Footer />
      </ContentArea>
    </PageWrapper>
  );
}

// Ölçüm/parite testlerinin üretim yolundaki geometriyi doğrudan çağırabilmesi
// için yeniden dışa verim (FE-08 raporundaki "390px → N px" ölçüsü bunu kullanır).
export { sidebarOffset };
